import React, { ComponentProps } from 'react'
import invariant from 'tiny-invariant'
import { typeValidator } from '@chilipiper/utils'
import { Box, Text, Flex } from '../../../core-components'
import { EndContent, StartContent, EndContentProvider, StartContentProvider } from '../../data'
import { ActionBarButton } from '../../action/action-bar/action-bar-button/ActionBarButton'
import { ActionBar } from '../../action/action-bar/ActionBar'

type Actions = Array<{
  'data-test-id'?: string
  onClick: () => void
  text: string
  variant: ComponentProps<typeof ActionBarButton>['variant']
}>

export const SectionHeader = ({
  title,
  startContent,
  endContent,
  description,
  hasDivider = true,
  actions,
}: {
  title: string
  startContent?: React.ReactNode
  endContent?: React.ReactNode
  description?: string
  hasDivider?: boolean
  actions?: Actions
}) => {
  invariant(
    startContent === undefined || typeValidator(StartContent)(startContent),
    'startContent must be a StartContent or undefined'
  )
  invariant(
    endContent === undefined || typeValidator(EndContent)(endContent),
    'endContent must be a EndContent or undefined'
  )

  return (
    <Flex
      gap={4}
      mx={5}
      boxShadow={hasDivider ? 'border/row' : undefined}
      justifyContent='space-between'
    >
      <Flex py={4} flexDirection='column' gap={2}>
        <Flex alignItems='center' minHeight={4} gap={3}>
          {startContent && (
            <StartContentProvider value={{ iconColor: 'fg/major' }}>
              {startContent}
            </StartContentProvider>
          )}
          <Text textStyle='page-section-heading' color='fg/major'>
            {title}
          </Text>
          {endContent && (
            <EndContentProvider value={{ iconColor: 'fg/major' }}>{endContent}</EndContentProvider>
          )}
        </Flex>
        {description && (
          <Text textStyle='body-multiline' color='fg/moderate'>
            {description}
          </Text>
        )}
      </Flex>
      {actions && (
        <Box mr={-5}>
          <ActionBar variant='inRow'>
            {actions.map(actionObject => (
              <ActionBar.Button
                key={actionObject.text}
                onClick={() => actionObject.onClick()}
                variant={actionObject.variant}
                data-test-id={actionObject['data-test-id']}
              >
                {actionObject.text}
              </ActionBar.Button>
            ))}
          </ActionBar>
        </Box>
      )}
    </Flex>
  )
}
